import { ApplicationInsights } from "@microsoft/applicationinsights-web";

const stripWrappingQuotes = (value) => {
  if (!value) {
    return value;
  }

  const trimmed = value.trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1).trim();
  }

  return trimmed;
};

const connectionString = stripWrappingQuotes(import.meta.env?.VITE_APPINSIGHTS_CONNECTION_STRING);
const disabledByEnv = (import.meta.env?.VITE_APPINSIGHTS_DISABLED ?? "").toLowerCase() === "true";

const hasInstrumentationKey = /(^|;)\s*InstrumentationKey\s*=\s*[^;]+/i.test(connectionString ?? "");

const rawSamplingPercentage = Number(import.meta.env?.VITE_APPINSIGHTS_SAMPLING_PERCENTAGE);
const samplingPercentage = Number.isFinite(rawSamplingPercentage) && rawSamplingPercentage > 0 && rawSamplingPercentage <= 100
  ? rawSamplingPercentage
  : 100;

let appInsightsClient = null;
let initialized = false;
let initializationFailed = false;

const isConfigured = Boolean(connectionString) && hasInstrumentationKey && !disabledByEnv;

const getClient = () => {
  if (!isConfigured || initializationFailed) {
    return null;
  }

  if (initialized && appInsightsClient) {
    return appInsightsClient;
  }

  try {
    appInsightsClient = new ApplicationInsights({
      config: {
        connectionString,
        samplingPercentage,
        enableAutoRouteTracking: false,
        disablePageUnloadEvents: ["unload"],
        disableFetchTracking: true,
        disableAjaxTracking: true,
      },
    });

    appInsightsClient.loadAppInsights();
    initialized = true;
    return appInsightsClient;
  } catch (error) {
    initializationFailed = true;
    appInsightsClient = null;

    if (import.meta.env?.DEV) {
      console.warn("[appInsights] Initialization skipped due to invalid configuration.", error);
    }

    return null;
  }
};

export const trackPageView = ({ name, uri }) => {
  try {
    const client = getClient();
    if (!client) {
      return;
    }

    client.trackPageView({ name, uri });
  } catch (error) {
    if (import.meta.env?.DEV) {
      console.warn("[appInsights] trackPageView failed and was ignored.", error);
    }
  }
};

export const isAppInsightsConfigured = isConfigured;